import { useContext, useEffect, useRef, useState } from "react";

import { Info } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  TextareaAutosize,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  BulletinResolutionCreation,
  createBulletinResolution,
  deleteBulletinResolution,
  deleteResolutionAttachment,
  getResolutionReasons,
  patchBulletinResolution,
  uploadResolutionAttachment,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { colors } from "@/common/colors";
import CustomizedSlider, {
  CustomizedSliderSlide,
} from "@/common/CustomizedSlider";
import TriUploadFile from "@/common/TriUploadFile";
import { isSmallScreen, mailTo } from "@/common/utils";
import MediaComponent from "@/components/alerts/add/UploadStep/MediaComponent";
import { BULLETIN_TYPE } from "@/components/alerts/constants";
import {
  AlertViewContext,
  SingleAlertContext,
} from "@/components/alerts/context";
import { DisabledComponentBanner } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { BulletinPatchPayload } from "@/types";
import {
  IBulletinResolution,
  IResolutionAttachment,
  IUserPermission,
} from "@/types/typesAlerts";

import styles from "./ResolveBulletin.module.scss";

interface ResolveBulletinItemProps {
  resolution: IBulletinResolution;
  bulletinType: BULLETIN_TYPE;
  setIsResolveOpen: (value: boolean) => void;
  readOnly?: boolean;
}

interface handleConfirmProps {
  bulletinId: number;
  resolution: IBulletinResolution;
  resolutionId: number;
}

const toResolutionPayload = (
  resolution: IBulletinResolution,
  published = resolution.published,
): BulletinResolutionCreation => ({
  reason: resolution.reason,
  other_reason: resolution.other_reason || null,
  details: resolution.details,
  resolved: resolution.resolved,
  published,
});

const responseResolution = (data: unknown): IBulletinResolution =>
  data as IBulletinResolution;

export const PublishedResolution = ({
  resolution,
}: {
  resolution: IBulletinResolution;
}) => {
  const attachments = Object.values(resolution.attachments);
  const getMediaUrl = (attachment: IResolutionAttachment) => {
    const url = new URL(attachment?.download_url ?? "");
    return url.toString();
  };

  const theme = useTheme();
  const isSmall = useMediaQuery((theme as Theme).breakpoints.down("sm"));
  const { t } = useTranslation("bulletin");

  const slides: CustomizedSliderSlide[] = attachments.map(
    (attachment, index) => ({
      id: `${attachment.filename}-${index}`,
      src: getMediaUrl(attachment),
      mediaType: attachment.content_type.includes("video") ? "video" : "image",
    }),
  );

  const getAuthorName = () => {
    if (!resolution.author) return t("resolve_unknown_author");
    const { first_name, last_name } = resolution.author;
    return (
      `${first_name || ""} ${last_name || ""}`.trim() ||
      t("resolve_unknown_author")
    );
  };

  return (
    <Paper
      style={{
        padding: "12px",
        marginBottom: "12px",
      }}
      elevation={4}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isSmall ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isSmall ? "flex-start" : "center",
          marginBottom: "8px",
          gap: "8px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Typography
            variant="h6"
            style={{
              fontWeight: "bold",
              margin: 0,
            }}
          >
            {resolution.reason !== "Other"
              ? resolution.reason
              : resolution.other_reason}
          </Typography>
          {resolution.resolved && (
            <Chip
              label={t("resolve_closed")}
              color={"primary"}
              size="small"
              style={{
                textTransform: "uppercase",
                fontSize: "0.7rem",
              }}
            />
          )}
        </div>
        <Typography
          variant="body2"
          style={{
            fontSize: "0.75rem",
            color: "GrayText",
            margin: 0,
          }}
        >
          {new Date(resolution.time_updated).toLocaleString()}
        </Typography>
      </div>

      {resolution.author && (
        <Tooltip
          title={resolution.author.email || "No email available"}
          placement="top"
          arrow
        >
          <Typography
            variant="body2"
            style={{
              fontSize: "0.8rem",
              color: colors.MUI_TEXT_SECONDARY,
              marginBottom: "8px",
              cursor: "pointer",
              marginLeft: "auto",
              textAlign: "right",
            }}
          >
            {t("resolve_comment_by_prefix")} {getAuthorName()}
            {resolution.author.badge_number &&
              ` (${t("resolve_badge")}: ${resolution.author.badge_number})`}
            {resolution.author.rank_short &&
              ` - ${resolution.author.rank_short}`}
          </Typography>
        </Tooltip>
      )}

      <Typography
        variant="body1"
        style={{
          marginBottom: attachments.length > 0 ? "12px" : "0",
          whiteSpace: "pre-wrap",
          fontSize: "0.9rem",
        }}
      >
        {resolution.details}
      </Typography>

      {attachments.length > 0 && (
        <div
          style={{
            width: "95%",
            margin: "auto",
          }}
        >
          <CustomizedSlider
            slides={slides}
            handleSlideClick={() => {}}
            maxSlidesPerView={{ desktop: 3, mobile: 1 }}
          />
        </div>
      )}
    </Paper>
  );
};

const ResolveBulletinItem = ({
  resolution,
  bulletinType,
  setIsResolveOpen,
  readOnly = false,
}: ResolveBulletinItemProps) => {
  const [updatedResolution, setResolution] = useState(resolution);
  const [initialResolution, setInitialResolution] = useState(resolution);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [attachments, setAttachments] = useState(resolution.attachments);
  const [isInfoIconOpen, setIsInfoIconOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isRevertDialogOpen, setIsRevertDialogOpen] = useState(false);
  const [shouldCancel, setShouldCancel] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    if (options.length > 0) return;

    setLoadingOptions(true);
    try {
      const res = await getResolutionReasons({
        query: { bulletin_type: bulletinType },
        throwOnError: true,
      });
      setOptions((res.data ?? []).map((r) => r.reason));
    } catch (error) {
      console.error("Error loading resolution reasons:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to load resolution reasons",
      );
    } finally {
      setLoadingOptions(false);
    }
  };
  const shouldCancelRef = useRef(shouldCancel);
  const { t } = useTranslation("bulletin");

  const wasPreviouslyStarted =
    !resolution.published &&
    (resolution.reason !== "" ||
      resolution.details !== "" ||
      Object.keys(resolution.attachments)?.length > 0);

  const {
    alertProperties: { bulletinId, resolutions },
    setAlertProperty,
  } = useContext(SingleAlertContext);
  const { setUpdatedItems } = useContext(AlertViewContext);

  // The bulletin is closed when its most recent published resolution is
  // resolved (same rule as the backend status filter and BulletinCard).
  const latestPublishedResolution = (resolutions ?? [])
    .filter((res) => res.published)
    .sort(
      (a, b) =>
        new Date(b.time_updated).getTime() - new Date(a.time_updated).getTime(),
    )[0];
  const isBulletinClosed = Boolean(latestPublishedResolution?.resolved);

  const fileInput = useRef(null);

  const [fieldErrors, setFieldErrors] = useState({});

  const hasChanges = () => {
    return (
      updatedResolution.reason !== initialResolution.reason ||
      updatedResolution.other_reason !== initialResolution.other_reason ||
      updatedResolution.details !== initialResolution.details ||
      updatedResolution.resolved !== initialResolution.resolved
    );
  };

  useEffect(() => {
    shouldCancelRef.current = shouldCancel;
  });

  const handleConfirm = ({
    bulletinId,
    resolution,
    resolutionId,
  }: handleConfirmProps) => {
    if (readOnly) return;
    const errors = {};

    if (!resolution.reason) errors["reason"] = t("resolve_reason_required");
    if (resolution.reason === "Other" && !resolution.other_reason)
      errors["other_reason"] = t("resolve_other_reason_required");

    setFieldErrors(errors);

    if (Object.keys(errors)?.length > 0) return;
    resolution.details = resolution.details.trim();

    const payload = toResolutionPayload(resolution, true);
    const request =
      resolutionId === -1
        ? createBulletinResolution({
            path: { bulletin_id: bulletinId },
            body: payload,
            throwOnError: true,
          })
        : patchBulletinResolution({
            path: { bulletin_id: bulletinId, resolution_id: resolutionId },
            body: payload,
            throwOnError: true,
          });

    request
      .then((response) => {
        const updatedResolution = responseResolution(response.data);
        const updatedResolutions =
          resolutionId === -1
            ? [...resolutions, updatedResolution]
            : resolutions.map((res) =>
                res.id === resolutionId ? updatedResolution : res,
              );
        setAlertProperty("resolutions", updatedResolutions);
        setInitialResolution(updatedResolution);
        // Signal the left-side bulletin list to refetch so the new comment
        // shows up on the card without a manual page refresh. The backend
        // re-applies the active filters; an empty marker is enough — AlertList
        // consumes updatedItems purely as a refresh flag.
        setUpdatedItems((prev) => [...prev, {} as BulletinPatchPayload]);
        toast.success(t("resolve_published_success"));
        setIsResolveOpen(false);
      })
      .catch((error) => {
        console.error("Error publishing resolution:", error);
        toast.error(error?.message || t("resolve_publish_failed"));
      });
  };

  const handleMediaUpload = async (event, files) => {
    setIsMediaUploading(true);

    let currentResolution = updatedResolution;
    if (currentResolution.id === -1 && bulletinId) {
      try {
        const response = await createBulletinResolution({
          path: { bulletin_id: bulletinId },
          body: toResolutionPayload(currentResolution, false),
          throwOnError: true,
        });
        const created = responseResolution(response.data);
        setAlertProperty("resolutions", [...resolutions, created]);
        setInitialResolution(created);
        setResolution(created);
        currentResolution = created;
      } catch (err) {
        console.error(err);
        setIsMediaUploading(false);
        return;
      }
    }

    for (let index = 0; index < files.length; index++) {
      if (bulletinId) {
        try {
          const response = await uploadResolutionAttachment({
            path: {
              bulletin_id: bulletinId,
              resolution_id: currentResolution.id,
            },
            body: { file: files[index] },
            throwOnError: true,
          });
          const res = responseResolution(response.data);

          if (shouldCancelRef.current) {
            // Cancelled uploading, break the loop
            handleAttachDelete(
              files[index].name,
              bulletinId,
              currentResolution.id,
            );
            break;
          }

          if (res) {
            setAlertProperty(
              "resolutions",
              resolutions.map((r) => (r.id === currentResolution.id ? res : r)),
            );
            setAttachments(res.attachments);
          } else {
            toast.error(t("resolve_image_upload_error"));
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
    setIsMediaUploading(false);

    if (event.target) {
      event.target.value = null;
    }
  };

  const handleAttachDelete = (
    fileName: string,
    bulletinID: number,
    resolutionID: number,
  ) => {
    deleteResolutionAttachment({
      path: {
        bulletin_id: bulletinID,
        resolution_id: resolutionID,
        filename: fileName,
      },
      throwOnError: true,
    }).then((response) => {
      const newResolution = responseResolution(response.data);
      const updatedResolutions = resolutions.map((res) =>
        res.id === resolutionID ? newResolution : res,
      );
      setAlertProperty("resolutions", updatedResolutions);
      setAttachments(newResolution.attachments);
    });
  };

  const handleReset = (bulletinId: number, resolutionId: number) => {
    if (resolutionId === -1) {
      setIsResolveOpen(false);
      return;
    }
    deleteBulletinResolution({
      path: { bulletin_id: bulletinId, resolution_id: resolutionId },
      throwOnError: true,
    }).then(() => {
      const updatedResolutions = resolutions.filter(
        (res) => res.id !== resolutionId,
      );
      setAlertProperty("resolutions", updatedResolutions);
      setInitialResolution(initialResolution);
      setResolution(initialResolution);
      setIsResolveOpen(false);
    });
  };

  const dialogs = {
    delete: {
      title: t("resolve_delete_title"),
      content: t("resolve_delete_content"),
      actions: [
        {
          label: t("resolve_cancel"),
          action: () => setIsDeleteDialogOpen(false),
        },
        {
          label: t("resolve_delete"),
          action: () => handleReset(bulletinId as number, updatedResolution.id),
        },
      ],
      open: isDeleteDialogOpen,
      onClose: () => setIsDeleteDialogOpen(false),
    },
    discardChanges: {
      title: t("resolve_revert_title"),
      content: t("resolve_revert_content"),
      actions: [
        {
          label: t("resolve_cancel"),
          action: () => {
            setIsRevertDialogOpen(false);
          },
        },
        {
          label: t("resolve_revert"),
          action: () => {
            setResolution(initialResolution);
            setIsRevertDialogOpen(false);
          },
        },
      ],
      open: isRevertDialogOpen,
      onClose: () => setIsRevertDialogOpen(false),
    },
    cancel: {
      title: t("resolve_cancel_title"),
      content: t("resolve_cancel_content"),
      actions: [
        {
          label: t("resolve_cancel"),
          action: () => setIsDeleteDialogOpen(false),
        },
        {
          label: t("resolve_confirm"),
          action: () => setIsResolveOpen(false),
        },
      ],
      open: isCancelDialogOpen,
      onClose: () => setIsCancelDialogOpen(false),
    },
  };

  const renderDialog = (dialog) => {
    return (
      <Dialog open={dialog.open} onClose={dialog.onClose}>
        <DialogTitle>{dialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialog.content}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {dialog.actions.map((action) => (
            <Button onClick={action.action} color="primary" key={action.label}>
              {action.label}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Paper
      style={{
        padding: "16px",
        marginBottom: "16px",
        display: "flex",
        flexDirection: "column",
      }}
      elevation={4}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          style={{
            fontWeight: "bold",
          }}
        >
          {t("resolve_comment_bulletin")}
        </Typography>
        <IconButton
          onClick={() => setIsInfoIconOpen(!isInfoIconOpen)}
          style={{ alignSelf: "flex-end" }}
        >
          <Info />
        </IconButton>
      </div>
      {wasPreviouslyStarted && (
        <Typography
          variant="body1"
          style={{
            marginBottom: "8px",
            color: "GrayText",
            fontSize: "0.8rem",
          }}
        >
          {t("resolve_previously_started")}
        </Typography>
      )}
      {isInfoIconOpen && (
        <Typography
          variant="body1"
          style={{
            marginBottom: "16px",
            color: colors.MUI_TEXT_SECONDARY,
            fontSize: "0.9rem",
          }}
        >
          {t("resolve_info_visible")}
        </Typography>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: isSmallScreen() ? "16px" : "0",
          gap: isSmallScreen() ? "0" : "16px",
          flexDirection: isSmallScreen() ? "column" : "row",
        }}
      >
        <Autocomplete
          open={open}
          onOpen={handleOpen}
          onClose={() => setOpen(false)}
          options={options}
          loading={loadingOptions}
          value={updatedResolution.reason || null}
          onChange={(event, value) =>
            setResolution({ ...updatedResolution, reason: value || "" })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("resolve_reason_label")}
              variant="outlined"
              required
              error={!!fieldErrors["reason"]}
              helperText={fieldErrors["reason"]}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptions ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          style={{
            width: isSmallScreen() ? "100%" : "50%",
          }}
          disabled={readOnly}
        />
        {updatedResolution.reason === "Other" && (
          <TextField
            value={updatedResolution?.other_reason}
            label={t("resolve_other_reason_label")}
            variant="outlined"
            style={{
              width: isSmallScreen() ? "100%" : "50%",
            }}
            onChange={(event) =>
              setResolution({
                ...updatedResolution,
                other_reason: event.target.value.slice(0, 30),
              })
            }
            required
            error={!!fieldErrors["other_reason"]}
            helperText={
              fieldErrors["other_reason"] ||
              (updatedResolution.other_reason?.length ?? 0) + "/30"
            }
            disabled={readOnly}
          />
        )}
      </div>
      <TextField
        label={t("resolve_comment_details_label")}
        variant="outlined"
        placeholder={t("resolve_comment_details_placeholder")}
        value={updatedResolution.details}
        error={!!fieldErrors["details"]}
        helperText={fieldErrors["details"]}
        onChange={(event) =>
          setResolution({ ...updatedResolution, details: event.target.value })
        }
        InputProps={{
          inputComponent: TextareaAutosize,
          inputProps: {
            minRows: 4,
            maxRows: 100,
            style: { resize: "none" },
          },
        }}
        disabled={readOnly}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          paddingTop: "16px",
        }}
      >
        {!isMediaUploading && (
          <TriUploadFile
            uploadMessage={t("resolve_upload_message")}
            fileInput={fileInput}
            handleFileUpload={handleMediaUpload}
            accept="image/*,video/*"
            multiple
            disabled={readOnly}
          />
        )}
        {isMediaUploading && (
          <>
            <Button
              variant="outlined"
              onClick={() => {
                setShouldCancel(true);
                setIsMediaUploading(false);
              }}
            >
              Cancel
            </Button>
          </>
        )}
      </div>
      {isMediaUploading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "16px",
          }}
        >
          <CircularProgress />
        </div>
      )}
      {attachments && bulletinId && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "16px",
            gap: "16px",
          }}
        >
          {Object.values(attachments).map((attachment) => (
            <MediaComponent
              key={attachment.download_url}
              handleFileDelete={() =>
                handleAttachDelete(
                  attachment.filename,
                  bulletinId,
                  updatedResolution.id,
                )
              }
              bulletinID={bulletinId}
              evidence={attachment}
              setEditMediaObject={() => {}}
              disableEdit={true}
              disableTags={true}
              disableOpenAiDescription={false}
            />
          ))}
        </div>
      )}
      {wasPreviouslyStarted && (
        <Typography
          variant="body1"
          style={{
            color: "GrayText",
            fontSize: "0.8rem",
            alignSelf: "flex-end",
          }}
        >
          {resolution.time_updated &&
            t("resolve_last_updated", {
              date: new Date(resolution.time_updated).toLocaleString(),
            })}
        </Typography>
      )}
      <div
        className="resolve-actions"
        style={{
          marginTop: "16px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={
              hasChanges() && wasPreviouslyStarted
                ? () => setIsRevertDialogOpen(true)
                : wasPreviouslyStarted
                  ? () => setIsDeleteDialogOpen(true)
                  : () => {
                      if (hasChanges()) {
                        setIsCancelDialogOpen(true);
                      } else {
                        setIsResolveOpen(false);
                      }
                    }
            }
          >
            {hasChanges() && wasPreviouslyStarted
              ? t("resolve_revert_changes")
              : wasPreviouslyStarted
                ? t("resolve_delete")
                : t("resolve_cancel")}
          </Button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "16px",
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              handleConfirm({
                bulletinId: bulletinId as number,
                resolution: {
                  ...updatedResolution,
                  resolved: isBulletinClosed,
                },
                resolutionId: updatedResolution.id,
              });
            }}
            disabled={readOnly}
          >
            {t("resolve_comment")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleConfirm({
                bulletinId: bulletinId as number,
                resolution: {
                  ...updatedResolution,
                  resolved: !isBulletinClosed,
                },
                resolutionId: updatedResolution.id,
              });
            }}
            disabled={readOnly}
          >
            {isBulletinClosed
              ? t("resolve_reopen_with_comment")
              : t("resolve_close_with_comment")}
          </Button>
        </div>
      </div>
      {Object.entries(dialogs).map(([key, dialog]) => (
        <div key={key}>{renderDialog(dialog)}</div>
      ))}
    </Paper>
  );
};

interface ResolveBulletinProps {
  bulletinType: BULLETIN_TYPE;
  bulletinId: number;
  setIsResolveOpen: (value: boolean) => void;
}

const ResolveBulletin = ({
  bulletinType,
  bulletinId,
  setIsResolveOpen,
}: ResolveBulletinProps) => {
  const {
    alertProperties: { resolutions },
  } = useContext(SingleAlertContext);
  const { currentUser } = useContext(AuthStateContext);
  const { t } = useTranslation("bulletin");

  const [unpublishedResolutions, setUnpublishedResolutions] = useState(
    resolutions?.filter((resolution) => !resolution.published),
  );

  const [alertPermissions] = useState(
    parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const readOnly = !alertPermissions.canCreateBulletin;

  const emptyResolution: IBulletinResolution = {
    id: -1,
    reason: "",
    other_reason: "",
    details: "",
    resolved: false,
    published: false,
    time_updated: new Date().toISOString(),
    attachments: {},
    bulletin_id: bulletinId,
    resolution_author_id: currentUser?.id ?? "",
    time_created: new Date().toISOString(),
  };

  useEffect(() => {
    const unpublished = resolutions?.filter((res) => !res.published) ?? [];
    setUnpublishedResolutions(
      unpublished.length > 0 ? unpublished : [emptyResolution],
    );
  }, [resolutions]);

  return (
    <div className={styles.resolveContainer}>
      {readOnly && (
        <DisabledComponentBanner
          handleClose={() => setIsResolveOpen(false)}
          title={t("resolve_no_permission_title")}
          message={
            <Typography>
              {t("resolve_no_permission_contact")}{" "}
              <Link
                rel="noopener noreferrer"
                target="_blank"
                href={mailTo({
                  email: "support@tryrubicon.com",
                  subject: "Requesting Access to Resolve Bulletins",
                  body: `Hello Rubicon Support Team, 

                  I would like to request access to resolve bulletins. I am trying to resolve a bulletin with id ${bulletinId}.

                  Thank you. 

                  Sincerely,

                  ${currentUser?.first_name} ${currentUser?.last_name} 
                  ${currentUser?.email}`,
                })}
                color="secondary"
              >
                support@tryrubicon.com
              </Link>{" "}
              {t("resolve_no_permission_suffix")}
            </Typography>
          }
        />
      )}
      {unpublishedResolutions?.map((resolution) => (
        <ResolveBulletinItem
          key={resolution.id}
          resolution={resolution}
          bulletinType={bulletinType}
          setIsResolveOpen={setIsResolveOpen}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
};

export default ResolveBulletin;
